import {Component, HostListener, inject} from "@angular/core";
import {NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import {SharedCodeActions} from "./shared/store/shared.actions";
import {UserSettingActions} from "./shared/user-setting/store/user-setting.actions";
import {MatBottomSheet, MatBottomSheetRef} from "@angular/material/bottom-sheet";
import {MatDialog} from "@angular/material/dialog";
import {CommentsOverlayService} from "./shared/comments/comments-overlay.service";
import {AiAssistantComponent} from "./shared/components/ai-assistant/ai-assistant.component";
import {AuthenticationService} from "./shared/services/login/authentication.service";
import {filter, Observable, of} from "rxjs";
import {LoadingService} from "./shared/services/loading/loading.service";
import {take} from "rxjs/operators";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {AiChatActions} from "./shared/store/ai-chat/ai-chat.actions";
import {selectAiChatIsOpen} from "./shared/store/ai-chat/ai-chat.selectors";

@Component({
    selector: "valumize-root",
    templateUrl: "./app.component.html",
    standalone: false
})
export class AppComponent {
    title = "valumize";

    private readonly store = inject(Store);
    private readonly bottomSheet = inject(MatBottomSheet);
    private readonly authService = inject(AuthenticationService);
    private readonly router = inject(Router);
    private readonly loadingService = inject(LoadingService);
    private readonly dialog = inject(MatDialog);
    private readonly commentsOverlayService = inject(CommentsOverlayService);

    isAuthenticated$: Observable<boolean> = of(false);
    isAiChatOpen$ = this.store.select(selectAiChatIsOpen);
    private bottomSheetRef: MatBottomSheetRef | null = null;

    constructor( ) {
        this.isAuthenticated$ = this.authService.isAuthenticated$;
        this.isAuthenticated$.pipe(filter(Boolean), take(1), takeUntilDestroyed()).subscribe(() => {
            this.store.dispatch(SharedCodeActions.loadCodeTables());
            this.store.dispatch(UserSettingActions.loadAll());
            // Definitions carry each setting's overridability, used e.g. by the weighting-divergence
            // popover to deep-link to My vs Organization settings (WI 1085).
            this.store.dispatch(UserSettingActions.loadDefinitions());
        });
        this.router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                this.loadingService.setNavigationLoading(true);
                // Close floating comment panels and any open modal so they don't linger over the
                // destination page. The requestOpen/deep-link open-on-arrival fires on NavigationEnd,
                // so this never fights it.
                this.commentsOverlayService.closeAll();
                this.dialog.closeAll();
            } else if (
                event instanceof NavigationEnd ||
                event instanceof NavigationCancel ||
                event instanceof NavigationError
            ) {
                this.loadingService.setNavigationLoading(false);
            }
        });
        // For Action Debugging, can be used in console with window.ngrxDumpActions()
        // installActionRateLogger(this.actions);
        // installActionTrace(this.actions, 300);
    }

    // Excel-style keyboard save: Ctrl+S (Win/Linux) / Cmd+S (Mac) saves the card currently in edit
    // mode by triggering its existing save button, so the card's exact save() logic and any
    // calculation effects run identically to a manual click. Only one card can be in edit mode at a
    // time (see selectIsAny{Asset,Deal,Fund}FormInEditMode), so at most one enabled save button exists.
    // The sibling Ctrl+K shortcut (focus the top-bar search) lives in SearchComponent.focusOnShortcut.
    @HostListener("document:keydown.control.s", ["$event"])
    @HostListener("document:keydown.meta.s", ["$event"])
    saveEditingCard(event: KeyboardEvent): void {
        // Don't save the card sitting behind an open modal dialog — it has its own save buttons.
        if (document.querySelector(".cdk-overlay-container mat-dialog-container")) {
            return;
        }

        // Only act when a card is in edit mode (it renders a save button, enabled or not). Otherwise
        // keep the browser default behaviour and don't disturb the currently focused element.
        if (!document.querySelector("button[data-test=\"save-button\"]")) {
            return;
        }

        event.preventDefault(); // suppress the browser "Save page as…" dialog

        // A mouse click on Save first blurs the focused input, flushing values that only commit on
        // blur (updateOn:"blur" controls and (blur) handlers). Ctrl+S keeps focus, so blur manually.
        (document.activeElement as HTMLElement | null)?.blur();

        // Re-query after blurring — flushing may re-validate the form and toggle the button's disabled state.
        const saveButton = document.querySelector<HTMLButtonElement>("button[data-test=\"save-button\"]:not(:disabled)");
        saveButton?.click();
    }

    toggleAiChat(): void {
        this.store.dispatch(AiChatActions.togglePanel());
    }

    triggerAiAssistant(): void {
        if (this.bottomSheetRef) {
            this.bottomSheetRef.dismiss();
            this.bottomSheetRef = null;
        } else {
            this.bottomSheetRef = this.bottomSheet.open(AiAssistantComponent);
            this.bottomSheetRef.afterDismissed().subscribe(() => {
                this.bottomSheetRef = null;
            });
        }
    }
}
